import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import ReelPlayer from '../components/ReelPlayer';
import { ArrowLeft, Film } from 'lucide-react';
import { Button } from '../components/ui/button';
import { sendReelEvent } from '../utils/reelEvents';

export default function Reels() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  
  const containerRef = useRef(null);
  const startY = useRef(0);
  const startTime = useRef(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);

  useEffect(() => {
    fetchReels();
  }, [searchParams]);

  const fetchReels = async () => {
    try {
      const role = searchParams.get('role');
      const store = searchParams.get('store');
      const tag = searchParams.get('tag');

      const params = new URLSearchParams();
      if (role) params.append('role', role);
      if (store) params.append('store', store);
      if (tag) params.append('tag', tag);
      params.append('limit', '20');

      const response = await api.get(`/reels?${params.toString()}`);
      
      // Handle both old format (array) and new format (object with reels array)
      const data = Array.isArray(response.data) ? response.data : response.data.reels || [];
      const cursor = response.data.next_cursor || null;
      const more = response.data.has_more || false;
      
      setReels(data);
      setNextCursor(cursor);
      setHasMore(more);
    } catch (error) {
      console.error('Failed to fetch reels:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreReels = useCallback(async () => {
    if (!hasMore || !nextCursor) return;
    
    try {
      const role = searchParams.get('role');
      const store = searchParams.get('store');
      const tag = searchParams.get('tag');

      const params = new URLSearchParams();
      if (role) params.append('role', role);
      if (store) params.append('store', store);
      if (tag) params.append('tag', tag);
      params.append('cursor', nextCursor);
      params.append('limit', '10');

      const response = await api.get(`/reels?${params.toString()}`);
      
      const data = Array.isArray(response.data) ? response.data : response.data.reels || [];
      const cursor = response.data.next_cursor || null;
      const more = response.data.has_more || false;
      
      if (data.length > 0) {
        setReels(prev => [...prev, ...data]);
        setNextCursor(cursor);
        setHasMore(more);
      }
    } catch (error) {
      console.error('Failed to fetch more reels:', error);
    }
  }, [searchParams, nextCursor, hasMore]);

  const goToNext = useCallback(() => {
    if (currentIndex < reels.length - 1) {
      const currentReel = reels[currentIndex];
      sendReelEvent(currentReel.id, 'reel_swipe_next', { method: 'swipe' });
      
      setCurrentIndex(prev => prev + 1);
      
      // Prefetch more when near end
      if (currentIndex >= reels.length - 3 && hasMore) {
        fetchMoreReels();
      }
    }
  }, [currentIndex, reels, hasMore, fetchMoreReels]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      const currentReel = reels[currentIndex];
      sendReelEvent(currentReel.id, 'reel_swipe_prev', { method: 'swipe' });
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex, reels]);

  // Velocity-based swipe handling
  const handleTouchStart = useCallback((e) => {
    startY.current = e.touches[0].clientY;
    startTime.current = Date.now();
    setIsSwiping(true);
    setSwipeOffset(0);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isSwiping) return;
    
    const currentY = e.touches[0].clientY;
    const diff = startY.current - currentY;
    
    // Update swipe offset for visual feedback
    setSwipeOffset(diff);

    // Prevent default scroll behavior for vertical swipes
    if (Math.abs(diff) > 10) {
      e.preventDefault();
    }
  }, [isSwiping]);

  const handleTouchEnd = useCallback((e) => {
    if (!isSwiping) return;

    const endY = e.changedTouches[0].clientY;
    const diff = startY.current - endY;
    const duration = Date.now() - startTime.current;
    
    // Calculate velocity (pixels per ms)
    const velocity = Math.abs(diff) / duration;
    
    // Velocity-based threshold: if fast swipe (> 0.5 px/ms) use 30px threshold
    // If slow swipe, use 80px threshold
    const threshold = velocity > 0.5 ? 30 : 80;
    
    // Swipe up = next, swipe down = prev
    if (diff > threshold) {
      goToNext();
    } else if (diff < -threshold) {
      goToPrev();
    }

    setIsSwiping(false);
    setSwipeOffset(0);
  }, [isSwiping, goToNext, goToPrev]);

  // Mouse wheel handling for desktop
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    
    // Throttle wheel events
    const now = Date.now();
    if (now - (window.lastWheelTime || 0) < 500) return;
    window.lastWheelTime = now;
    
    if (e.deltaY > 0) {
      goToNext();
    } else if (e.deltaY < 0) {
      goToPrev();
    }
  }, [goToNext, goToPrev]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrev]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-black">
        <div className="text-white text-xl">Loading reels...</div>
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-black text-white p-8">
        <h2 className="text-2xl font-bold mb-4">Geen Reels Beschikbaar</h2>
        <p className="text-gray-400 mb-8">Kom later terug voor nieuwe content</p>
        <Button onClick={() => window.history.back()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-screen w-screen overflow-hidden bg-black"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      data-testid="reels-container"
    >
      {/* Back button - with safe area */}
      <div 
        className="absolute left-4 z-50"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
      >
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full bg-black/30 text-white hover:bg-black/50 backdrop-blur-sm"
          onClick={() => window.history.back()}
          data-testid="reels-back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      </div>

      {/* Reel counter + Course badge - with safe area */}
      <div 
        className="absolute right-4 z-50 flex flex-col items-end gap-2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
      >
        {/* Course badge if current item is a playlist */}
        {reels[currentIndex]?.kind === 'reel_playlist' && (
          <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm flex items-center gap-1">
            <Film className="h-3 w-3" />
            Course
          </div>
        )}
        
        {/* Counter */}
        <div className="bg-black/30 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
          {currentIndex + 1} / {reels.length}
          {hasMore && '+'}
        </div>
      </div>

      {/* Reels stack */}
      <div 
        className="relative h-full w-full transition-transform duration-300 ease-out"
        style={{
          transform: `translateY(${-currentIndex * 100 + (isSwiping ? -swipeOffset / 10 : 0)}%)`,
        }}
      >
        {reels.map((reel, index) => {
          // Only render current, previous, and next reel for performance
          const shouldRender = Math.abs(index - currentIndex) <= 1;
          
          return (
            <div
              key={reel.id}
              className="absolute inset-0"
              style={{
                transform: `translateY(${index * 100}%)`,
                display: shouldRender ? 'block' : 'none'
              }}
            >
              {shouldRender && (
                <>
                  {reel.kind === 'reel_playlist' ? (
                    // Course playlist - show poster with play button
                    <div 
                      className="w-full h-full bg-black flex flex-col items-center justify-center cursor-pointer"
                      onClick={() => navigate(`/reels/course/${reel.id}`)}
                    >
                      <div className="text-center p-8 max-w-2xl">
                        <div className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 mb-6">
                          <Film className="h-4 w-4" />
                          Course Reel
                        </div>
                        
                        <h2 className="text-white text-3xl md:text-5xl font-bold mb-4">{reel.title}</h2>
                        
                        {reel.description && (
                          <p className="text-white/80 text-lg mb-6">{reel.description}</p>
                        )}
                        
                        <div className="flex items-center justify-center gap-4 mb-8">
                          <div className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm">
                            {reel.total_blocks || 0} blocks
                          </div>
                          {reel.roles && reel.roles.length > 0 && (
                            <div className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm">
                              {reel.roles.join(', ')}
                            </div>
                          )}
                        </div>
                        
                        <Button 
                          size="lg"
                          className="bg-white text-black hover:bg-gray-200 text-lg px-8 py-6"
                        >
                          <Film className="h-5 w-5 mr-2" />
                          Start Course
                        </Button>
                        
                        <p className="text-white/60 text-sm mt-4">
                          Tap om te starten • Swipe voor volgende
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Regular single video reel
                    <ReelPlayer
                      reel={reel}
                      isActive={index === currentIndex}
                      onNext={goToNext}
                      onPrev={goToPrev}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Loading indicator for prefetch */}
      {hasMore && currentIndex >= reels.length - 5 && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
          Laden...
        </div>
      )}
    </div>
  );
}
